import React, { useState, useEffect } from 'react';
import { hyposAPI, hypersAPI, dexcomAPI } from '../../services/api';
import { useHypoStore } from '../../store/hypoStore';
import { GroupedHypoEvent, GroupedHyperEvent, HypoTreatment } from '../../types';

export const HypoList: React.FC = () => {
  const { hypos, setHypos, setLoading, setError } = useHypoStore();
  const [hypers, setHypers] = useState<GroupedHyperEvent[]>([]);
  const [treatments, setTreatments] = useState<{ [key: number]: HypoTreatment }>({});
  const [days, setDays] = useState(7);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const hyposResponse = await hyposAPI.getHypos(days);
      const hypersResponse = await hypersAPI.getHypers(days);

      setHypos(hyposResponse.data);
      setHypers(hypersResponse.data);
      setError(null);

      // Charger les traitements pour chaque hypo
      const treatmentMap: { [key: number]: HypoTreatment } = {};
      for (const hypo of hyposResponse.data) {
        try {
          const treatmentResponse = await hyposAPI.getHypoDetail(hypo.id);
          if (treatmentResponse.data.treatment) {
            treatmentMap[hypo.id] = treatmentResponse.data.treatment;
          }
        } catch (err) {
          // Traitement non trouvé, c'est OK
        }
      }
      setTreatments(treatmentMap);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur de récupération');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await dexcomAPI.sync();
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleTreatmentSuccess = (hypoId: number, treatment: HypoTreatment) => {
    setTreatments((prev) => ({
      ...prev,
      [hypoId]: treatment,
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Glycémie - Hypos & Hypers</h2>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
        >
          {syncing ? 'Synchronisation...' : 'Synchroniser Dexcom'}
        </button>
      </div>

      <div className="flex space-x-4">
        <label className="flex items-center space-x-2">
          <input
            type="radio"
            checked={days === 7}
            onChange={() => setDays(7)}
            className="form-radio"
          />
          <span className="text-gray-700">7 jours</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="radio"
            checked={days === 14}
            onChange={() => setDays(14)}
            className="form-radio"
          />
          <span className="text-gray-700">14 jours</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="radio"
            checked={days === 30}
            onChange={() => setDays(30)}
            className="form-radio"
          />
          <span className="text-gray-700">30 jours</span>
        </label>
      </div>

      {hypos.length === 0 && hypers.length === 0 ? (
        <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg text-center">
          <p className="text-gray-600">Aucun événement détecté</p>
          <p className="text-sm text-gray-500 mt-2">
            Synchronisez avec Dexcom pour récupérer les dernières données
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {hypos.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-red-600 mb-3">
                🔴 Hypoglycémies ({hypos.length})
              </h3>
              <div className="space-y-3">
                {hypos.map((hypo: GroupedHypoEvent) => (
                  <HypoCard
                    key={hypo.id}
                    hypo={hypo}
                    treatment={treatments[hypo.id]}
                    onTreatmentSuccess={(treatment) =>
                      handleTreatmentSuccess(hypo.id, treatment)
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {hypers.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-orange-600 mb-3">
                ⚠️ Hypergycémies ({hypers.length})
              </h3>
              <div className="space-y-3">
                {hypers.map((hyper: GroupedHyperEvent) => (
                  <HyperCard key={hyper.id} hyper={hyper} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface HypoCardProps {
  hypo: GroupedHypoEvent;
  treatment?: HypoTreatment;
  onTreatmentSuccess: (treatment: HypoTreatment) => void;
}

const HypoCard: React.FC<HypoCardProps> = ({ hypo, treatment, onTreatmentSuccess }) => {
  const [showTreatment, setShowTreatment] = useState(false);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });
  };

  return (
    <div className="bg-white p-4 rounded-lg border border-red-200 hover:shadow-md transition">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">
                {hypo.glucose_min}
              </div>
              <div className="text-xs text-gray-500">Min</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">
                {hypo.glucose_avg.toFixed(1)}
              </div>
              <div className="text-xs text-gray-500">Moyen</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">
                {hypo.glucose_max}
              </div>
              <div className="text-xs text-gray-500">Max</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {hypo.duration_minutes} min
              </div>
              <div className="text-xs text-gray-500">Durée</div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {formatDate(hypo.detected_at)} · {formatTime(hypo.detected_at)} à {formatTime(hypo.ended_at)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {hypo.readings_count} mesure{hypo.readings_count > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowTreatment(!showTreatment)}
          className="text-indigo-600 hover:text-indigo-700 font-medium ml-4"
        >
          {showTreatment ? 'Masquer' : treatment ? 'Modifier' : 'Traitement'}
        </button>
      </div>

      {showTreatment && (
        <TreatmentForm
          hypoId={hypo.id}
          existingTreatment={treatment}
          onSuccess={(t) => {
            setShowTreatment(false);
            onTreatmentSuccess(t);
          }}
        />
      )}
    </div>
  );
};

interface HyperCardProps {
  hyper: GroupedHyperEvent;
}

const HyperCard: React.FC<HyperCardProps> = ({ hyper }) => {
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });
  };

  return (
    <div className="bg-white p-4 rounded-lg border border-orange-200 hover:shadow-md transition">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">
                {hyper.glucose_min}
              </div>
              <div className="text-xs text-gray-500">Min</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">
                {hyper.glucose_avg.toFixed(1)}
              </div>
              <div className="text-xs text-gray-500">Moyen</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">
                {hyper.glucose_max}
              </div>
              <div className="text-xs text-gray-500">Max</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {hyper.duration_minutes} min
              </div>
              <div className="text-xs text-gray-500">Durée</div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {formatDate(hyper.detected_at)} · {formatTime(hyper.detected_at)} à {formatTime(hyper.ended_at)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {hyper.readings_count} mesure{hyper.readings_count > 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

interface TreatmentFormProps {
  hypoId: number;
  existingTreatment?: HypoTreatment;
  onSuccess: (treatment: HypoTreatment) => void;
}

const TreatmentForm: React.FC<TreatmentFormProps> = ({
  hypoId,
  existingTreatment,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    treatment_type: existingTreatment?.treatment_type || 'glucose_tablet',
    quantity: existingTreatment?.quantity || 15,
    unit: existingTreatment?.unit || 'g',
    notes: existingTreatment?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'quantity' ? parseFloat(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let response;
      if (existingTreatment) {
        // Modifier le traitement existant
        response = await hyposAPI.updateTreatment(
          hypoId,
          existingTreatment.id,
          formData
        );
      } else {
        // Ajouter un nouveau traitement
        response = await hyposAPI.addTreatment(hypoId, formData);
      }
      onSuccess(response.data);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        (existingTreatment
          ? 'Erreur lors de la modification'
          : 'Erreur lors de l\'ajout')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
      {error && (
        <div className="p-2 bg-red-100 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <select
            name="treatment_type"
            value={formData.treatment_type}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="glucose_tablet">Comprimés glucose</option>
            <option value="juice">Jus</option>
            <option value="candy">Bonbons</option>
            <option value="honey">Miel</option>
            <option value="other">Autre</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Quantité
          </label>
          <div className="flex space-x-2">
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              min="0"
              step="0.5"
            />
            <select
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="g">g</option>
              <option value="ml">ml</option>
              <option value="piece">pcs</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes (optionnel)
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none"
          rows={2}
          placeholder="Ex: 2 comprimés + jus..."
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-indigo-600 text-white py-2 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition"
      >
        {loading
          ? existingTreatment
            ? 'Modification...'
            : 'Enregistrement...'
          : existingTreatment
          ? 'Modifier le traitement'
          : 'Enregistrer le traitement'}
      </button>
    </form>
  );
};
